import type {
  CustomField,
  CustomFieldValue,
  CustomFieldValueInput,
  CustomFieldType,
} from '@sales-center/common'
import type { ICustomFieldValueFormModel } from './typings'

export function customValuesToInputModel<
  FormModel extends ICustomFieldValueFormModel,
>(model: FormModel, customFields: CustomField[]): CustomFieldValueInput[] {
  const { customFields: customFieldValues } = model

  const result: CustomFieldValueInput[] = []

  const flattenedFieldValues = {
    ...customFieldValues.text,
    ...customFieldValues.date,
    ...customFieldValues.bool,
    ...customFieldValues.multi,
    ...customFieldValues.single,
    ...customFieldValues.number,
  }

  for (const fieldId in flattenedFieldValues) {
    const field = customFields.find((x) => x.id === Number(fieldId))

    if (!!field) {
      const { id = -1, value } = flattenedFieldValues[fieldId]

      let valid: boolean = false

      const toAdd: CustomFieldValueInput = {
        customFieldId: field.id,
        customFieldValueId: id === -1 ? undefined : id,
      }

      if (value !== undefined && value !== null && value !== '') {
        switch (field.type) {
          case 'Text':
            valid = true
            toAdd.text = value as string
            break
          case 'Date':
            valid = true
            toAdd.date = value as Date
            break
          case 'Checkbox':
            valid = true
            toAdd.bool = value as boolean
            break
          case 'DropdownMultiSelect':
            toAdd.customFieldOptionIds = value as number[]
            valid = toAdd.customFieldOptionIds.length > 0
            break
          case 'DropdownSingleSelect':
            valid = true
            toAdd.customFieldOptionId = value as number
            break
          case 'Number':
            valid = true
            toAdd.number = value as number
            break
        }
      }

      if (valid) {
        result.push(toAdd)
      }
    }
  }

  return result
}

export function customValuesToFormModel(
  customFields: CustomField[],
  fieldValues: CustomFieldValue[] = [],
) {
  const initialValues: ICustomFieldValueFormModel['customFields'] = {
    text: {},
    date: {},
    bool: {},
    multi: {},
    single: {},
    number: {},
  }

  // set inital values to null
  customFields.map((field) => {
    initialValues[customFieldTypeToFormType(field.type)][field.id] = {
      value: null,
    }
  })

  // hydrate with passed values
  return fieldValues.reduce<ICustomFieldValueFormModel['customFields']>(
    (acc, fieldValue) => {
      const { customFieldId, id } = fieldValue

      switch (fieldValue.type) {
        case 'Text':
          acc.text[customFieldId] = { id, value: fieldValue.text }
          break

        case 'Date':
          acc.date[customFieldId] = { id, value: fieldValue.date }
          break

        case 'Checkbox':
          acc.bool[customFieldId] = { id, value: fieldValue.bool }
          break

        case 'DropdownMultiSelect':
          const opts = fieldValue.customFieldValueOptions?.map(
            (o) => o.customFieldOptionId,
          )
          acc.multi[customFieldId] = { id, value: opts ?? null }
          break

        case 'DropdownSingleSelect':
          const opt = fieldValue.customFieldOptionId
          acc.single[customFieldId] = { id, value: opt }
          break

        case 'Number':
          acc.number[customFieldId] = {
            id,
            value: fieldValue.number?.toString() ?? null,
          }
          break
      }

      return acc
    },
    initialValues,
  )
}

export function customFieldTypeToFormType(
  type: CustomFieldType,
): keyof ICustomFieldValueFormModel['customFields'] {
  switch (type) {
    case 'Checkbox':
      return 'bool'
    case 'Date':
      return 'date'
    case 'DropdownMultiSelect':
      return 'multi'
    case 'DropdownSingleSelect':
      return 'single'
    case 'Text':
      return 'text'
    case 'Number':
      return 'number'
  }
}
